// Pure presentation helpers for the /email-eval Runs tab (kanban 90c7a83f).
// Kept out of the page component so they are unit-testable without rendering.

import type { CaseResult, EvalRun, EvalRunKind, FullPipelineVerdict } from '../services/emailEval';

export interface Badge {
  label: string;
  bg: string;
  fg: string;
}

const PASS: Badge = { label: 'PASS', bg: '#dcfce7', fg: '#166534' };
const FAIL: Badge = { label: 'FAIL', bg: '#fef2f2', fg: '#991b1b' };
const HELD: Badge = { label: 'HELD', bg: '#fef3c7', fg: '#92400e' };
const NO_ORDER: Badge = { label: 'NO ORDER', bg: '#f3f4f6', fg: '#374151' };

/** Human label for a run's harness. */
export function runKindLabel(kind: EvalRunKind | undefined): string {
  return kind === 'full_pipeline' ? 'Full pipeline · Merlin' : 'Extraction · LLM judge';
}

export function runKindBadge(kind: EvalRunKind | undefined): Badge {
  return kind === 'full_pipeline'
    ? { label: runKindLabel(kind), bg: '#ede9fe', fg: '#5b21b6' }
    : { label: runKindLabel(kind), bg: '#e0f2fe', fg: '#075985' };
}

/**
 * The verdict badge for one case row. Extraction rows are binary (ok →
 * PASS/FAIL); full_pipeline rows carry the 4-state verdict where HELD means
 * "wrong but every problem was flagged needs_review" — safe, not a failure.
 */
export function verdictBadge(result: Pick<CaseResult, 'ok' | 'verdict'>, kind: EvalRunKind | undefined): Badge {
  if (kind === 'full_pipeline' && result.verdict) {
    const map: Record<FullPipelineVerdict, Badge> = {
      pass: PASS,
      held: HELD,
      fail: FAIL,
      no_order: NO_ORDER,
    };
    return map[result.verdict] ?? (result.ok ? PASS : FAIL);
  }
  return result.ok ? PASS : FAIL;
}

/** Pass rate as a whole percentage, or null when the run has no cases. */
export function passRate(summary: EvalRun['summary']): number | null {
  if (!summary.total) return null;
  return Math.round((summary.passed / summary.total) * 100);
}

/**
 * Whether a human may override a case verdict. Only LLM-judged extraction
 * runs take spot-check overrides; full_pipeline verdicts are deterministic
 * reconciliation (the API refuses too).
 */
export function canSpotCheck(run: Pick<EvalRun, 'run_kind' | 'status'>): boolean {
  return run.status === 'completed' && run.run_kind !== 'full_pipeline';
}

/** Short trigger/environment line for the run header. */
export function runProvenance(run: Pick<EvalRun, 'run_kind' | 'trigger_source' | 'environment' | 'extractor'>): string {
  const parts: string[] = [];
  if (run.environment) parts.push(`env ${run.environment}`);
  if (run.trigger_source) parts.push(`via ${run.trigger_source.replace('_', ' ')}`);
  if (run.run_kind === 'full_pipeline' && run.extractor) parts.push(`extractor ${run.extractor}`);
  return parts.join(' · ');
}
